// src/components/operations/ScheduledTab.jsx
// Scheduled jobs tab with timeline and list views
import { useState, useEffect, useMemo, useCallback } from 'react';
import { useGraphQL } from '../../graphql';
import BatchJobCreator from '../BatchJobCreator';
import Modal from '../Modal';
import {
    CalendarDaysIcon,
    PlayIcon,
    PauseIcon,
    PlusIcon,
    ListBulletIcon,
    XMarkIcon,
    BoltIcon,
    PencilIcon,
    CheckIcon,
} from '@heroicons/react/24/outline';

// Category definitions with colors
const JOB_CATEGORIES = {
    'check_new': { label: 'Check New', color: '#3B82F6', short: 'CHK' },
    'crawl': { label: 'Crawl', color: '#F97316', short: 'CRL' },
    'deep_crawl': { label: 'Deep Crawl', color: '#EF4444', short: 'DCP' },
    'assess_tender': { label: 'Assess', color: '#A855F7', short: 'ASS' },
    'prequalify_tenders': { label: 'Prequalify', color: '#EAB308', short: 'PRQ' },
    'index_all_tenders': { label: 'Index All', color: '#14B8A6', short: 'IDX' },
    'convert_all_documents': { label: 'Convert All', color: '#6366F1', short: 'CNV' },
    'geocode_all': { label: 'Geocode All', color: '#78716C', short: 'GEO' },
    'run_workflow': { label: 'Run Workflow', color: '#8B5CF6', short: 'WFL' },
    'run_company_workflows': { label: 'Company Workflows', color: '#7C3AED', short: 'CWF' },
    'send_saved_search_reports': { label: 'Send Reports', color: '#EC4899', short: 'RPT' },
    'sync_index': { label: 'Sync Index', color: '#06B6D4', short: 'SYN' },
    'generate_narratives_bulk': { label: 'Narratives Bulk', color: '#D946EF', short: 'NAR' },
    'generate_facet_analyses_bulk': { label: 'Facets Bulk', color: '#C026D3', short: 'FAC' },
    'cleanup_expired_downloads': { label: 'Download Cleanup', color: '#F59E0B', short: 'DLC' },
    'send_renewal_reminders': { label: 'Renewal Reminders', color: '#10B981', short: 'REN' },
    'send_trial_reminders': { label: 'Trial Reminders', color: '#22C55E', short: 'TRI' },
    'backfill_dedup_keys': { label: 'Dedup Backfill', color: '#64748B', short: 'DDP' },
    'backfill_sbund_details': { label: 'Sbund Backfill', color: '#475569', short: 'SBD' },
};

const ScheduledTab = ({ autoRefresh, refreshTrigger, onRefresh }) => {
    const { executeQuery, executeMutation, queries, mutations, loading } = useGraphQL();

    // State
    const [jobs, setJobs] = useState([]);
    const [selectedJob, setSelectedJob] = useState(null);
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [showBatchCreator, setShowBatchCreator] = useState(false);
    const [viewMode, setViewMode] = useState('list'); // 'timeline' | 'list'
    const [actionResult, setActionResult] = useState(null);
    const [filterCategory] = useState('all');
    const [filterEnabled, setFilterEnabled] = useState('all');

    // Edit schedule modal state
    const [editMode, setEditMode] = useState(false);
    const [editCron, setEditCron] = useState('');
    const [editParams, setEditParams] = useState('');
    const [editParamsError, setEditParamsError] = useState(null);
    const [editSaving, setEditSaving] = useState(false);

    // Load scheduled jobs
    const loadScheduledJobs = useCallback(async () => {
        try {
            const data = await executeQuery(queries.getJobs(), { limit: 200, offset: 0, isScheduled: true });
            const scheduledJobs = (data.adminJobs || []).filter(job => job.isScheduled);
            setJobs(scheduledJobs);
        } catch (err) {
            console.error('Failed to load scheduled jobs:', err);
        }
    }, [executeQuery, queries]);

    useEffect(() => {
        loadScheduledJobs();
    }, [refreshTrigger]);

    useEffect(() => {
        if (!autoRefresh) return;
        const interval = setInterval(loadScheduledJobs, 10000);
        return () => clearInterval(interval);
    }, [autoRefresh, loadScheduledJobs]);

    const showMessage = (message, isError = false) => {
        setActionResult({ message, isError });
        setTimeout(() => setActionResult(null), 4000);
    };

    const enableJob = async (jobId) => {
        try {
            const result = await executeMutation(mutations.adminEnableScheduledJob, { jobId });
            if (result.adminEnableScheduledJob?.success) {
                showMessage('Job enabled');
                loadScheduledJobs();
            } else {
                showMessage(result.adminEnableScheduledJob?.error || 'Failed', true);
            }
        } catch (err) {
            showMessage(err.message, true);
        }
    };

    const disableJob = async (jobId) => {
        try {
            const result = await executeMutation(mutations.adminDisableScheduledJob, { jobId });
            if (result.adminDisableScheduledJob?.success) {
                showMessage('Job disabled');
                loadScheduledJobs();
            } else {
                showMessage(result.adminDisableScheduledJob?.error || 'Failed', true);
            }
        } catch (err) {
            showMessage(err.message, true);
        }
    };

    // Manually trigger a scheduled job — creates (or re-uses pending/running) child run.
    // Dedup: if a child is already pending/running the backend returns that existing child
    // with success=true. Disabled schedules can still be triggered (for debugging).
    const triggerJob = async (jobId) => {
        try {
            const result = await executeMutation(mutations.adminTriggerScheduledJob, { jobId });
            const r = result.adminTriggerScheduledJob;
            if (r?.success) {
                showMessage(`Triggered — child job ${r.jobId || ''}${r.message ? ': ' + r.message : ''}`.trim());
                loadScheduledJobs();
            } else {
                showMessage(r?.error || 'Failed to trigger scheduled job', true);
            }
        } catch (err) {
            showMessage(err.message, true);
        }
    };

    // Parse job.parameters (string or object) into a plain object, or null if unparseable
    const parseJobParameters = (job) => {
        if (!job?.parameters) return null;
        try {
            return typeof job.parameters === 'string' ? JSON.parse(job.parameters) : job.parameters;
        } catch { return null; }
    };

    // Compact one-line JSON preview (truncated to ~80 chars) for the list view cell
    const getParametersPreview = (job) => {
        const obj = parseJobParameters(job);
        if (obj == null) return '';
        const s = JSON.stringify(obj);
        return s.length > 80 ? s.slice(0, 80) + '…' : s;
    };

    // Full pretty-printed JSON for the cell's title tooltip
    const getParametersFull = (job) => {
        const obj = parseJobParameters(job);
        if (obj == null) return '';
        return JSON.stringify(obj, null, 2);
    };

    // YAML-managed rows (V39) are owned by the schedule reconciler
    // (tenders_be workflows/_schedule.yaml). Cron/param edits to them are
    // reverted on the next reconcile, so the UI doesn't offer editing —
    // change the YAML and run reconcileSchedule instead. Enable/disable and
    // manual triggering stay available as incident levers (with the revert
    // caveat), since they don't pretend to be durable configuration.
    const isYamlManaged = (job) => job?.managedBy === 'yaml';

    // Open the edit form for a given job (seeds cron + pretty-printed params)
    const openEditMode = (job) => {
        if (isYamlManaged(job)) {
            showMessage('YAML-managed schedule — edit workflows/_schedule.yaml and reconcile instead', true);
            return;
        }
        setEditCron(job.cronSchedule || '');
        const parsed = parseJobParameters(job);
        setEditParams(parsed ? JSON.stringify(parsed, null, 2) : '');
        setEditParamsError(null);
        setEditMode(true);
    };

    const cancelEditMode = () => {
        setEditMode(false);
        setEditParamsError(null);
    };

    // Save changes — adminUpdateScheduledJob accepts partial updates; we omit
    // unchanged fields so a backend that treats null as "clear" isn't surprised.
    const saveEdit = async () => {
        if (!selectedJob) return;
        let parsedParams;
        const trimmed = editParams.trim();
        if (trimmed === '' || trimmed === '{}') {
            parsedParams = null;
        } else {
            try {
                parsedParams = JSON.parse(trimmed);
            } catch (err) {
                setEditParamsError(`Invalid JSON: ${err.message}`);
                return;
            }
        }

        const variables = { jobId: selectedJob.id };
        const originalCron = selectedJob.cronSchedule || '';
        if (editCron !== originalCron) variables.cronSchedule = editCron || null;
        const originalParamsObj = parseJobParameters(selectedJob);
        const originalParamsStr = originalParamsObj ? JSON.stringify(originalParamsObj) : '';
        const newParamsStr = parsedParams ? JSON.stringify(parsedParams) : '';
        if (newParamsStr !== originalParamsStr) variables.parameters = parsedParams;

        if (Object.keys(variables).length === 1) {
            // Only jobId — nothing changed
            setEditMode(false);
            return;
        }

        setEditSaving(true);
        try {
            const result = await executeMutation(mutations.adminUpdateScheduledJob, variables);
            const r = result.adminUpdateScheduledJob;
            if (r?.success) {
                showMessage('Schedule updated');
                setEditMode(false);
                loadScheduledJobs();
                // Keep the modal open but refresh selectedJob via the next loadScheduledJobs tick
            } else {
                showMessage(r?.error || 'Failed to update schedule', true);
            }
        } catch (err) {
            showMessage(err.message, true);
        } finally {
            setEditSaving(false);
        }
    };

    // Parse cron to get next run times
    const parseCronSchedule = (cronSchedule) => {
        if (!cronSchedule) return { description: 'Unknown', hour: 0, minute: 0 };
        const parts = cronSchedule.split(' ');
        if (parts.length < 5) return { description: cronSchedule, hour: 0, minute: 0 };

        const [minute, hour] = parts;
        let description = '';

        if (minute.startsWith('*/')) {
            description = `Every ${minute.slice(2)} min`;
        } else if (hour.startsWith('*/')) {
            description = `Every ${hour.slice(2)} hours`;
        } else {
            description = `${hour.padStart(2, '0')}:${minute.padStart(2, '0')}`;
        }

        return {
            description,
            hour: parseInt(hour) || 0,
            minute: parseInt(minute) || 0,
        };
    };

    // Get category for job type
    const getCategory = (jobType) => {
        const normalizedType = jobType?.toLowerCase().replace(/-/g, '_');
        return JOB_CATEGORIES[normalizedType] || { label: jobType, color: '#6B7280', short: jobType?.substring(0, 3).toUpperCase() };
    };

    // Filter jobs
    const filteredJobs = useMemo(() => {
        return jobs.filter(job => {
            if (filterEnabled === 'enabled' && !job.enabled) return false;
            if (filterEnabled === 'disabled' && job.enabled) return false;
            if (filterCategory !== 'all') {
                const category = getCategory(job.type);
                if (category.short !== filterCategory) return false;
            }
            return true;
        });
    }, [jobs, filterCategory, filterEnabled]);

    // Group jobs by hour for timeline view
    const jobsByHour = useMemo(() => {
        const grouped = {};
        for (let i = 0; i < 24; i++) {
            grouped[i] = [];
        }

        filteredJobs.forEach(job => {
            const parsed = parseCronSchedule(job.cronSchedule);
            // For "every X minutes" jobs, show them in all hours
            if (job.cronSchedule?.includes('*/')) {
                for (let i = 0; i < 24; i++) {
                    grouped[i].push(job);
                }
            } else {
                grouped[parsed.hour]?.push(job);
            }
        });

        return grouped;
    }, [filteredJobs]);

    // Stats
    const stats = useMemo(() => ({
        total: jobs.length,
        enabled: jobs.filter(j => j.enabled).length,
        disabled: jobs.filter(j => !j.enabled).length,
    }), [jobs]);

    return (
        <div className="h-full flex flex-col bg-gray-950">
            {/* Batch Job Creator Modal */}
            {showBatchCreator && (
                <BatchJobCreator
                    isOpen={showBatchCreator}
                    onClose={() => setShowBatchCreator(false)}
                    onCreated={() => { loadScheduledJobs(); onRefresh?.(); }}
                />
            )}

            {/* Job Detail Modal */}
            <Modal
                isOpen={showDetailModal}
                onClose={() => {
                    setShowDetailModal(false);
                    setSelectedJob(null);
                    setEditMode(false);
                    setEditParamsError(null);
                }}
                title={editMode ? 'Edit Scheduled Job' : 'Scheduled Job Details'}
            >
                {selectedJob && !editMode && (
                    <div className="space-y-4">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <div
                                    className="w-3 h-3 rounded"
                                    style={{ backgroundColor: getCategory(selectedJob.type).color }}
                                />
                                <span className="font-mono text-sm font-bold text-slate-900">
                                    {getCategory(selectedJob.type).label}
                                </span>
                            </div>
                            <div className="flex items-center gap-2">
                                {isYamlManaged(selectedJob) && (
                                    <span className="px-2 py-0.5 rounded bg-violet-100 border border-violet-200 font-mono text-xs font-bold text-violet-700">
                                        YAML-MANAGED
                                    </span>
                                )}
                                <span className={`px-2 py-0.5 rounded font-mono text-xs font-bold ${
                                    selectedJob.enabled ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                                }`}>
                                    {selectedJob.enabled ? 'ENABLED' : 'DISABLED'}
                                </span>
                            </div>
                        </div>

                        {isYamlManaged(selectedJob) && (
                            <div className="px-3 py-2 bg-violet-50 border border-violet-200 font-mono text-xs text-violet-700">
                                Owned by <span className="font-bold">workflows/_schedule.yaml</span>
                                {selectedJob.managedName ? <> as <span className="font-bold">{selectedJob.managedName}</span></> : null}.
                                Cron and parameters can only be changed in the YAML (then Reconcile Schedule in the Workflows tab).
                                Enable/disable here is temporary — the next reconcile restores the YAML state.
                            </div>
                        )}

                        <div className="space-y-2">
                            <div>
                                <div className="font-mono text-xs text-slate-400 uppercase">Job ID</div>
                                <div className="font-mono text-xs text-slate-900 break-all">{selectedJob.id}</div>
                            </div>
                            <div>
                                <div className="font-mono text-xs text-slate-400 uppercase">Cron Schedule</div>
                                <div className="font-mono text-sm text-slate-900">{selectedJob.cronSchedule}</div>
                            </div>
                            {selectedJob.lastScheduledRun && (
                                <div>
                                    <div className="font-mono text-xs text-slate-400 uppercase">Last Run</div>
                                    <div className="font-mono text-xs text-slate-900">
                                        {new Date(selectedJob.lastScheduledRun).toLocaleString()}
                                    </div>
                                </div>
                            )}
                            {selectedJob.nextScheduledRun && (
                                <div>
                                    <div className="font-mono text-xs text-slate-400 uppercase">Next Run</div>
                                    <div className="font-mono text-xs text-slate-900">
                                        {new Date(selectedJob.nextScheduledRun).toLocaleString()}
                                    </div>
                                </div>
                            )}
                        </div>

                        {selectedJob.parameters && (
                            <div>
                                <div className="font-mono text-xs text-slate-400 uppercase mb-2">Parameters</div>
                                <pre className="surface-card p-2 font-mono text-xs text-slate-700 overflow-x-auto max-h-48 overflow-y-auto">
                                    {(() => {
                                        if (typeof selectedJob.parameters !== 'string') return JSON.stringify(selectedJob.parameters, null, 2);
                                        try { return JSON.stringify(JSON.parse(selectedJob.parameters), null, 2); }
                                        catch { return selectedJob.parameters; }
                                    })()}
                                </pre>
                            </div>
                        )}

                        <div className="flex gap-2 pt-4 border-t border-slate-200">
                            <button
                                onClick={() => { triggerJob(selectedJob.id); setShowDetailModal(false); }}
                                className="flex-1 px-4 py-2 rounded-lg bg-amber-600 text-on-accent font-mono text-xs font-bold hover:bg-amber-500 transition-all duration-150"
                                title="Manually trigger this scheduled job (creates a child run)"
                            >
                                <BoltIcon className="h-4 w-4 inline mr-1" />
                                RUN NOW
                            </button>
                            {!isYamlManaged(selectedJob) && (
                                <button
                                    onClick={() => openEditMode(selectedJob)}
                                    className="flex-1 px-4 py-2 rounded-lg bg-cyan-600 text-on-accent font-mono text-xs font-bold hover:bg-cyan-500 transition-all duration-150"
                                    title="Edit cron schedule and parameters"
                                >
                                    <PencilIcon className="h-4 w-4 inline mr-1" />
                                    EDIT
                                </button>
                            )}
                            {selectedJob.enabled ? (
                                <button
                                    onClick={() => { disableJob(selectedJob.id); setShowDetailModal(false); }}
                                    className="flex-1 px-4 py-2 rounded-lg bg-yellow-500 text-on-accent font-mono text-xs font-bold hover:bg-yellow-400 transition-all duration-150"
                                >
                                    <PauseIcon className="h-4 w-4 inline mr-1" />
                                    DISABLE
                                </button>
                            ) : (
                                <button
                                    onClick={() => { enableJob(selectedJob.id); setShowDetailModal(false); }}
                                    className="flex-1 px-4 py-2 rounded-lg bg-green-600 text-on-accent font-mono text-xs font-bold hover:bg-green-500 transition-all duration-150"
                                >
                                    <PlayIcon className="h-4 w-4 inline mr-1" />
                                    ENABLE
                                </button>
                            )}
                        </div>
                    </div>
                )}

                {selectedJob && editMode && (
                    <div className="space-y-4">
                        <div className="flex items-center gap-2">
                            <div
                                className="w-3 h-3 rounded"
                                style={{ backgroundColor: getCategory(selectedJob.type).color }}
                            />
                            <span className="font-mono text-sm font-bold text-slate-900">
                                {getCategory(selectedJob.type).label}
                            </span>
                            <span className="font-mono text-xs text-slate-500 ml-2">{selectedJob.id}</span>
                        </div>

                        <div>
                            <div className="font-mono text-xs text-gray-600 uppercase mb-1">Cron Schedule</div>
                            <input
                                type="text"
                                value={editCron}
                                onChange={(e) => setEditCron(e.target.value)}
                                className="w-full bg-gray-800 border border-gray-700 text-white p-2 font-mono text-sm"
                                placeholder="minute hour day month weekday — e.g. 0 2 * * *"
                            />
                            <div className="font-mono text-xs text-gray-500 mt-1">
                                Format: minute hour day month weekday. Leave blank to clear.
                            </div>
                        </div>

                        <div>
                            <div className="font-mono text-xs text-gray-600 uppercase mb-1">Parameters (JSON)</div>
                            <textarea
                                value={editParams}
                                onChange={(e) => { setEditParams(e.target.value); setEditParamsError(null); }}
                                rows={12}
                                className="w-full bg-gray-800 border border-gray-700 text-white p-2 font-mono text-xs"
                                placeholder="{}"
                                spellCheck="false"
                            />
                            {editParamsError && (
                                <div className="mt-1 px-2 py-1 bg-red-50 border border-red-200 font-mono text-xs text-red-700">
                                    {editParamsError}
                                </div>
                            )}
                        </div>

                        <div className="flex gap-2 pt-4 border-t border-slate-200">
                            <button
                                onClick={saveEdit}
                                disabled={editSaving}
                                className="flex-1 px-4 py-2 rounded-lg bg-primary text-on-accent font-mono text-xs font-bold hover:brightness-110 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-150"
                            >
                                <CheckIcon className="h-4 w-4 inline mr-1" />
                                {editSaving ? 'SAVING…' : 'SAVE'}
                            </button>
                            <button
                                onClick={cancelEditMode}
                                disabled={editSaving}
                                className="flex-1 px-4 py-2 rounded-lg bg-white text-slate-700 border border-slate-200 font-mono text-xs font-bold hover:bg-slate-50 hover:border-slate-300 disabled:opacity-50 transition-all duration-150"
                            >
                                CANCEL
                            </button>
                        </div>
                    </div>
                )}
            </Modal>

            {/* Header */}
            <div className="border-b border-gray-800 bg-gray-900 px-4 py-3">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => setViewMode('list')}
                                className={`p-1.5 rounded-lg transition-all duration-150 ${viewMode === 'list' ? 'bg-primary text-on-accent' : 'text-slate-500 hover:text-slate-900 hover:bg-slate-50'}`}
                                title="List view"
                            >
                                <ListBulletIcon className="h-4 w-4" />
                            </button>
                            <button
                                onClick={() => setViewMode('timeline')}
                                className={`p-1.5 rounded-lg transition-all duration-150 ${viewMode === 'timeline' ? 'bg-primary text-on-accent' : 'text-slate-500 hover:text-slate-900 hover:bg-slate-50'}`}
                                title="Timeline view"
                            >
                                <CalendarDaysIcon className="h-4 w-4" />
                            </button>
                        </div>

                        <div className="font-mono text-xs text-gray-500">
                            {stats.total} scheduled • {stats.enabled} enabled • {stats.disabled} disabled
                        </div>
                    </div>

                    <div className="flex items-center gap-2">
                        {/* Filter by enabled/disabled */}
                        <select
                            value={filterEnabled}
                            onChange={(e) => setFilterEnabled(e.target.value)}
                            className="bg-gray-800 text-gray-300 border border-gray-700 px-2 py-1 font-mono text-xs"
                        >
                            <option value="all">All Status</option>
                            <option value="enabled">Enabled</option>
                            <option value="disabled">Disabled</option>
                        </select>

                        <button
                            onClick={() => setShowBatchCreator(true)}
                            className="px-3 py-1.5 rounded-lg bg-white text-slate-700 text-xs font-mono border border-slate-200 hover:bg-slate-50 hover:border-slate-300 transition-all duration-150"
                        >
                            <PlusIcon className="h-4 w-4 inline mr-1" />
                            BATCH CREATE
                        </button>
                    </div>
                </div>

                {/* Action result banner */}
                {actionResult && (
                    <div className={`mt-2 px-3 py-2 font-mono text-xs font-bold flex items-center justify-between ${
                        actionResult.isError ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'
                    }`}>
                        <span>{actionResult.message}</span>
                        <button onClick={() => setActionResult(null)}>
                            <XMarkIcon className="h-4 w-4" />
                        </button>
                    </div>
                )}
            </div>

            {/* Content */}
            <div className="flex-1 overflow-auto">
                {viewMode === 'timeline' ? (
                    /* Timeline View */
                    <div className="p-4">
                        <div className="grid grid-cols-24 gap-px bg-gray-800 border border-gray-700">
                            {/* Hour headers */}
                            {Array.from({ length: 24 }, (_, i) => (
                                <div key={i} className="bg-gray-900 px-1 py-2 text-center">
                                    <div className="font-mono text-xs text-gray-500">
                                        {i.toString().padStart(2, '0')}
                                    </div>
                                    <div className="mt-2 space-y-1">
                                        {jobsByHour[i]?.slice(0, 5).map(job => {
                                            const category = getCategory(job.type);
                                            return (
                                                <button
                                                    key={job.id}
                                                    onClick={() => { setSelectedJob(job); setShowDetailModal(true); }}
                                                    className={`w-full px-1 py-0.5 font-mono text-xs truncate ${
                                                        job.enabled ? 'opacity-100' : 'opacity-50'
                                                    }`}
                                                    style={{ backgroundColor: category.color, color: '#000' }}
                                                    title={`${category.label} - ${job.cronSchedule}`}
                                                >
                                                    {category.short}
                                                </button>
                                            );
                                        })}
                                        {(jobsByHour[i]?.length || 0) > 5 && (
                                            <div className="font-mono text-xs text-gray-500">
                                                +{jobsByHour[i].length - 5}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                ) : (
                    /* List View */
                    <table className="w-full">
                        <thead className="sticky top-0 bg-gray-900 border-b border-gray-800">
                            <tr className="font-mono text-xs text-gray-500 uppercase">
                                <th className="text-left px-4 py-2 font-medium">Type</th>
                                <th className="text-left px-4 py-2 font-medium">Schedule</th>
                                <th className="text-left px-4 py-2 font-medium">Parameters</th>
                                <th className="text-left px-4 py-2 font-medium">Status</th>
                                <th className="text-left px-4 py-2 font-medium">Last Run</th>
                                <th className="text-left px-4 py-2 font-medium">Next Run</th>
                                <th className="text-left px-4 py-2 font-medium">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-900">
                            {filteredJobs.map(job => {
                                const category = getCategory(job.type);
                                const parsed = parseCronSchedule(job.cronSchedule);

                                return (
                                    <tr
                                        key={job.id}
                                        onClick={() => { setSelectedJob(job); setShowDetailModal(true); }}
                                        className="cursor-pointer hover:bg-gray-900/50 transition-colors"
                                    >
                                        <td className="px-4 py-2">
                                            <div className="flex items-center gap-2">
                                                <div className="w-2 h-2 rounded-sm" style={{ backgroundColor: category.color }} />
                                                <span className="font-mono text-xs text-slate-900">{category.label}</span>
                                                {isYamlManaged(job) && (
                                                    <span
                                                        className="px-1.5 py-0.5 rounded bg-violet-100 border border-violet-200 font-mono text-[10px] font-bold text-violet-700"
                                                        title={`Owned by workflows/_schedule.yaml (${job.managedName || 'unnamed'}) — edits are reverted on reconcile`}
                                                    >
                                                        YAML
                                                    </span>
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-4 py-2">
                                            <div className="font-mono text-xs text-slate-700">{job.cronSchedule}</div>
                                            <div className="font-mono text-xs text-slate-500">{parsed.description}</div>
                                        </td>
                                        <td
                                            className="px-4 py-2 font-mono text-xs text-slate-700 max-w-xs truncate"
                                            title={getParametersFull(job) || undefined}
                                        >
                                            {getParametersPreview(job) || <span className="text-slate-400">-</span>}
                                        </td>
                                        <td className="px-4 py-2">
                                            <span className={`px-2 py-0.5 rounded font-mono text-xs font-bold ${
                                                job.enabled ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                                            }`}>
                                                {job.enabled ? 'ENABLED' : 'DISABLED'}
                                            </span>
                                        </td>
                                        <td className="px-4 py-2 font-mono text-xs text-gray-400">
                                            {job.lastScheduledRun ? new Date(job.lastScheduledRun).toLocaleString() : '-'}
                                        </td>
                                        <td className="px-4 py-2 font-mono text-xs text-gray-400">
                                            {job.nextScheduledRun ? new Date(job.nextScheduledRun).toLocaleString() : '-'}
                                        </td>
                                        <td className="px-4 py-2">
                                            <div className="flex items-center gap-1">
                                                <button
                                                    onClick={(e) => { e.stopPropagation(); triggerJob(job.id); }}
                                                    className="p-1 text-gray-500 hover:text-amber-700"
                                                    title="Run now (trigger this scheduled job)"
                                                >
                                                    <BoltIcon className="h-4 w-4" />
                                                </button>
                                                {!isYamlManaged(job) && (
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setSelectedJob(job);
                                                            openEditMode(job);
                                                            setShowDetailModal(true);
                                                        }}
                                                        className="p-1 text-gray-500 hover:text-cyan-700"
                                                        title="Edit schedule"
                                                    >
                                                        <PencilIcon className="h-4 w-4" />
                                                    </button>
                                                )}
                                                {job.enabled ? (
                                                    <button
                                                        onClick={(e) => { e.stopPropagation(); disableJob(job.id); }}
                                                        className="p-1 text-gray-500 hover:text-yellow-700"
                                                        title="Disable"
                                                    >
                                                        <PauseIcon className="h-4 w-4" />
                                                    </button>
                                                ) : (
                                                    <button
                                                        onClick={(e) => { e.stopPropagation(); enableJob(job.id); }}
                                                        className="p-1 text-gray-500 hover:text-green-700"
                                                        title="Enable"
                                                    >
                                                        <PlayIcon className="h-4 w-4" />
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                            {filteredJobs.length === 0 && (
                                <tr>
                                    <td colSpan="7" className="px-4 py-8 text-center text-gray-600 font-mono text-sm">
                                        {loading ? 'Loading...' : 'No scheduled jobs found'}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                )}
            </div>

            {/* Category Legend */}
            <div className="border-t border-gray-800 bg-gray-900 px-4 py-2 flex flex-wrap gap-2">
                {Object.entries(JOB_CATEGORIES).slice(0, 10).map(([key, cat]) => (
                    <div key={key} className="flex items-center gap-1">
                        <div className="w-2 h-2" style={{ backgroundColor: cat.color }} />
                        <span className="font-mono text-xs text-gray-500">{cat.short}</span>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default ScheduledTab;
